// The install dialog's words before and after Add (keepr-web
// InstallPackageModal.js, split out of it by KPR-1710 for the file-size
// ratchet): the refusal a person can act on, the install preview and the
// add-on preview (keepr-api KPR-445 / KPR-632), and who sees which elements a
// package keeps (keepr-api KPR-1710). Presentational: plain text and lists
// only, so each reads in order on a screen reader and wraps at 375 px.
import React from 'react';
import { Alert } from 'antd';

import PlanLimitAlert from '../common/PlanLimitAlert';
import { apiErrorMessage } from '../../utils/apiErrors';
import { planLimitOf } from '../../utils/planLimit';
import { keptFieldLabel, keptFieldsNote, notAppliedWords } from '../../utils/packageElementRules';

/** The one refusal a person can act on, in the platform's words. */
export function refusalText(error) {
	const code = error?.response?.data?.code;
	if (code === 'package_already_installed') return 'This is already in that collection. Choose another, or make a new one.';
	if (code === 'package_key_taken') {
		const taken = error.response.data.taken || [];
		// Since KPR-357 a key the collection already has is installed beside
		// it under a suffixed key; what is still refused cannot be (a package
		// set sharing a global set's key), so renaming yours would not help.
		return `That collection already has ${taken.join(', ') || 'a card with the same name'}, and this package can't be added beside it. Choose another collection, or make a new one.`;
	}
	if (code === 'email_unverified') return 'Confirm your email address first — adding a package is one of the things that waits for it.';
	// A bundle's add-ons (keepr-api KPR-383): the server's words name the
	// add-on and the card it is missing.
	if (['addon_already_installed', 'addon_core_missing', 'addon_needs', 'addon_unknown', 'package_not_installed'].includes(code)) {
		return error.response.data.message || 'That add-on could not be added.';
	}
	// A card that can use a card you have (keepr-api KPR-1811, 1812, 1813).
	if (BINDING_WORDS[code]) return BINDING_WORDS[code](error.response.data);
	return apiErrorMessage(error, 'The package could not be added.');
}

const firstReason = data => (Array.isArray(data.problems) && data.problems.length ? ` ${data.problems[0].reason || ''}`.trimEnd() : '');

/** The refusals of a bundle that uses a card you have, in plain words. Nothing was written for any of them. */
const BINDING_WORDS = {
	requirement_unbound: () => 'This bundle can use a card you already have. Choose what it uses before adding it.',
	binding_needs_approval: () => 'Some of what it needs from your card is still to choose.',
	binding_invalid: data => `What it would use on your card doesn't fit.${firstReason(data)}`,
	binding_card_not_allowed: () => 'That card can\'t be used here: it isn\'t one of this collection\'s own cards. Choose another, or add a new one.',
	binding_name_unusable: data => `A name on your card can't be used where this bundle needs it.${firstReason(data)}`,
	binding_collision: data => `Something it would add clashes with your card.${firstReason(data)}`,
	stale_card: () => 'Your card changed while you were choosing. Check the matches again, then add it.',
	binding_needs_person: () => 'The changes to your card need you to approve them in keepr.',
	install_record_busy: () => 'This bundle was being changed at the same moment. Nothing was kept on your card. Try again.',
	preview_stale: () => 'Something changed since this was checked. Check it again, then add it.',
};

const capitalized = text => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Who sees which element (keepr-api KPR-1710): the elements the package keeps
 * from some people — each rule switched on as soon as it is added — and, from
 * the preview, any it would not apply here and why (a rule this collection
 * already has is kept; the server's switch off). Plain text and lists, as the
 * previews above.
 */
export function KeptElements({ rules, notApplied }) {
	if (!rules.length && !notApplied.length) return null;
	return (
		<section className="install-package__kept" aria-label="Who sees which elements" data-testid="install-package-element-rules">
			{rules.length ? (
				<>
					<p className="meta-line install-preview__line">{keptFieldsNote(rules.length)}</p>
					<ul className="install-preview__list">
						{rules.map(rule => <li key={`${rule.card?.ref || rule.card?.name}:${rule.element}:${rule.column || ''}`}>{`${keptFieldLabel(rule)}${rule.card?.name ? ` on ${rule.card.name}` : ''}: ${rule.summary}`}</li>)}
					</ul>
				</>
			) : null}
			{notApplied.length ? (
				<>
					<p className="meta-line install-preview__line">{notApplied.length === 1 ? 'One would not be kept as the package says:' : `${notApplied.length} would not be kept as the package says:`}</p>
					<ul className="install-preview__list">
						{notApplied.map(entry => <li key={`${entry.card?.name}:${entry.element}:${entry.column || ''}:${entry.reason}`}>{notAppliedWords(entry)}</li>)}
					</ul>
				</>
			) : null}
		</section>
	);
}

/**
 * The preview's words (keepr-api KPR-445), as the services' `installPreviewNotes`
 * made them: the install's own refusal when it would be refused, else what
 * would be added, kept and left out. Plain text and lists only, so it reads in
 * order on a screen reader and wraps at 375 px; nothing here is a control but
 * the retry in the dialog.
 */
export function InstallPreview({ notes, collectionName, showExampleDates }) {
	// A plan limit the package would pass (KPR-1092's package pre-flight):
	// the PlanLimit pattern, worded for whoever is adding it.
	if (notes.refusal && planLimitOf(notes.refusal)) {
		return <PlanLimitAlert refusal={notes.refusal} className="install-preview__refusal" />;
	}
	if (notes.refusal) {
		return <Alert type="warning" showIcon className="install-preview__refusal" message={refusalText({ response: { data: notes.refusal } })} />;
	}
	return (
		<div className="install-preview__body">
			{notes.clear ? <p className="meta-line install-preview__line">{`Nothing in ${collectionName} is in its way.`}</p> : null}
			{notes.keys ? <p className="meta-line install-preview__line">{notes.keys}</p> : null}
			{notes.adds ? <p className="meta-line install-preview__line">{notes.adds}</p> : null}
			{notes.reused ? <p className="meta-line install-preview__line">{notes.reused}</p> : null}
			{notes.renamed.length ? (
				<>
					<p className="meta-line install-preview__line">{notes.renamed.length === 1 ? 'One of its names is already yours. Yours stays as it is; the package\'s is added with its name after it:' : `${notes.renamed.length} of its names are already yours. Yours stay as they are; the package's are added with its name after them:`}</p>
					<ul className="install-preview__list">
						{notes.renamed.map(r => <li key={`${r.noun}:${r.to}`}>{`${capitalized(r.noun)} "${r.from}" is added as "${r.to}".${r.restricted ? ' It is restricted.' : ''}`}</li>)}
					</ul>
				</>
			) : null}
			{notes.leftOut.length ? (
				<>
					<p className="meta-line install-preview__line">{notes.leftOut.length === 1 ? 'One thing would be left out:' : `${notes.leftOut.length} things would be left out:`}</p>
					<ul className="install-preview__list">
						{notes.leftOut.map(x => <li key={`${x.label}:${x.reason}`}>{`${x.label}: ${x.reason}`}</li>)}
					</ul>
				</>
			) : null}
			{notes.overview ? <p className="meta-line install-preview__line">{notes.overview}</p> : null}
			{showExampleDates && notes.exampleDates ? <p className="meta-line install-preview__line">Example dates start from today</p> : null}
			{notes.consent ? <p className="meta-line install-preview__line">{notes.consent}</p> : null}
		</div>
	);
}

/**
 * The add-on preview's words (keepr-api KPR-632), as the services'
 * `addOnPreviewNotes` made them: the call's own refusal when it would be
 * refused, else the cards it would add, what it would put on the cards,
 * dashboards and forms already there, its own settings, the examples it
 * would bring, and what it would leave off and why. Plain text and lists
 * only, as InstallPreview.
 */
export function AddOnPreview({ notes }) {
	if (notes.refusal) {
		return <Alert type="warning" showIcon className="install-preview__refusal" message={refusalText({ response: { data: notes.refusal } })} />;
	}
	return (
		<div className="install-preview__body">
			{notes.cards ? <p className="meta-line install-preview__line">{notes.cards}</p> : null}
			{notes.places.length ? (
				<ul className="install-preview__list">
					{notes.places.map(line => <li key={line}>{line}</li>)}
				</ul>
			) : null}
			{notes.settings ? <p className="meta-line install-preview__line">{notes.settings}</p> : null}
			{notes.examples ? <p className="meta-line install-preview__line">{notes.examples}</p> : null}
			{notes.leftOut.length ? (
				<>
					<p className="meta-line install-preview__line">{notes.leftOut.length === 1 ? 'Left out, one thing:' : `Left out, ${notes.leftOut.length} things:`}</p>
					<ul className="install-preview__list">
						{notes.leftOut.map(x => <li key={`${x.label}:${x.reason}`}>{`${x.label}, because ${x.reason}.`}</li>)}
					</ul>
				</>
			) : null}
		</div>
	);
}
